import {IconButton} from "@mui/material";
import {Theme} from "@mui/material/styles";
import {SxProps} from "@mui/system";
import {isUndefined} from "lodash";
import React, {ReactNode} from "react";
import {PaymentPlanFrequency} from "../../../enums";
import {formatCurrency, formatShortenNumber, prettifyCurrencyRounded} from "../../../helpers";
import {Card, Chip, ChipType, CircleDot, Colors, Container, Text} from "../../../ui";
import {PaymentPlanOffer} from "../../declarations";
import {useIsMobile} from "../../helpers";
import {mapFrequencyToSuggestionsLabels} from "./utils";

interface ISummaryText {
  field: string;
  value: ReactNode;
  icon?: ReactNode;
  colorIndicator?: string;
  lineCross?: boolean;
  onClickIcon?: () => void;
}

export interface IPlanSuggestion {
  index: number;
  firstAmount: number;
  duration: number;
  secondAmount?: number;
  discount?: number;
  selected: boolean;
  handleSelect: (index: number) => void;
  frequency?: PaymentPlanFrequency;
  isSingleSuggestion?: boolean;
  sx?: SxProps<Theme>;
  showMostAffordableChip?: boolean;
}

interface IHardshipSuggestion {
  index: number;
  selected: boolean;
  minimal_hardship: number;
  handleSelect: (index: number) => void;
  isPlanPickerFaq?: boolean;
}

interface ITextLabel {
  title: string;
  subtitle?: string;
  icon: ReactNode;
}

export interface ISelectPlan {
  index: number;
  paymentPlanOffer: PaymentPlanOffer | undefined;
}

interface IPlanSuggestionCard {
  onClick?: () => void;
  discount?: number;
  isSingleSuggestion?: boolean;
  selected?: boolean;
  children?: ReactNode;
  sx?: SxProps<Theme>;
}

const SummarySectionTitle = ({title}: {title: string}) => {
  return (
    <Text
      size={14}
      variant={"inter600"}
      sx={{textTransform: "uppercase", color: Colors.grey._500, letterSpacing: "0.5px"}}
    >
      {title}
    </Text>
  );
};

const SummaryText = ({field, value, colorIndicator, icon, lineCross = false, onClickIcon}: ISummaryText) => {
  return (
    <Container row fullWidth sx={{justifyContent: "space-between"}}>
      <Text sx={{textAlign: "start"}}>{field}</Text>
      <Container row sx={{textAlign: "end"}}>
        <Container row sx={{gap: "2px"}}>
          {colorIndicator && <CircleDot color={colorIndicator} />}
          <Text fontWeight={"bold"} sx={{...(lineCross && {textDecoration: "line-through"})}}>
            {value}
          </Text>
        </Container>
        {icon && (
          <IconButton onClick={onClickIcon} sx={{padding: "0px"}}>
            {icon}
          </IconButton>
        )}
      </Container>
    </Container>
  );
};

const BulletText = ({text}: {text: string}) => {
  return (
    <Container fullWidth row sx={{alignItems: "start", padding: "0px 10px"}}>
      <Text size={14} variant={"inter400"} sx={{color: Colors.secondary._800}}>
        &bull;
      </Text>
      <Text size={14} variant={"inter400"} sx={{color: Colors.secondary._800}}>
        {text}
      </Text>
    </Container>
  );
};

const PlanSuggestion = ({
  index,
  firstAmount,
  duration,
  secondAmount,
  discount,
  selected,
  handleSelect,
  frequency = "monthly",
  isSingleSuggestion = false,
  showMostAffordableChip = false,
}: IPlanSuggestion) => {
  const {label, shortLabel} = mapFrequencyToSuggestionsLabels[frequency];
  const onClick = () => {
    handleSelect(index);
  };
  return (
    <PlanSuggestionCard
      selected={selected}
      discount={discount}
      onClick={onClick}
      isSingleSuggestion={isSingleSuggestion}
    >
      <Text variant={"inter600"} sx={{display: "inline"}}>
        {prettifyCurrencyRounded(firstAmount)}/{shortLabel}{" "}
        <Text
          variant={secondAmount ? "inter600" : "inter400"}
          sx={{display: "inline"}}
        >{`for ${duration} ${label} `}</Text>
        {secondAmount && (
          <Text variant={"inter400"} sx={{display: "inline"}}>
            {`, then ${prettifyCurrencyRounded(secondAmount)}/${shortLabel}`}
          </Text>
        )}
      </Text>
      {showMostAffordableChip && (
        <Chip label={"Most affordable"} variant_={"info"} size_={"small"} />
      )}
      {!!discount && (
        <Chip
          label={`-${discount}%`}
          variant_={"secondary"}
          size_={"small"}
        />
      )}
    </PlanSuggestionCard>
  );
};

const PlanSuggestionCard = ({
  onClick,
  discount,
  isSingleSuggestion = false,
  selected,
  children,
  sx,
}: IPlanSuggestionCard) => {
  const isMobile = useIsMobile();
  return (
    <Card
      row
      onClick={onClick}
      sx={{
        width: isMobile ? "100%" : "530px",
        justifyContent: isUndefined(discount) ? "center" : "space-between",
        border: selected && !isSingleSuggestion ? `2px solid ${Colors.primary._800}` : `1px solid ${Colors.grey._200}`,
        padding: "18px",
        textAlign: "start",
        gap: "0px",
        ...sx,
      }}
    >
      {children}
    </Card>
  );
};

const HardshipSuggestion = ({selected, index, handleSelect, minimal_hardship, isPlanPickerFaq}: IHardshipSuggestion) => {
  const onClick = () => {
    handleSelect(index);
  };
  const formattedAmount = `$${formatShortenNumber(formatCurrency(minimal_hardship))}/mo`;
  return (
    <PlanSuggestionCard selected={selected} discount={0} onClick={onClick} isSingleSuggestion={false}>
      <Text variant={"inter600"} sx={{display: "inline"}}>
        {isPlanPickerFaq ? (
          <>
            {formattedAmount} <Text variant={"inter400"} sx={{display: "inline"}}>then increase</Text>
          </>
        ) : (
          "Hardship options"
        )}
      </Text>
      <Chip
        label={isPlanPickerFaq ? "Hardship options" : `From ${formattedAmount}`}
        variant_={"info"}
        size_={"small"}
      />
    </PlanSuggestionCard>
  );
};

const TextLabel = ({title, subtitle, icon}: ITextLabel) => {
  const isMobile = useIsMobile();

  return (
    <Container row sx={{textAlign: "start", alignItems: "start", width: isMobile ? "100%" : "320px"}}>
      <div>{icon}</div>
      <Text variant={"inter600"} sx={{display: "inline"}}>
        {title}
        {subtitle && (
          <Text variant={"inter400"} sx={{display: "inline"}}>
            {subtitle}
          </Text>
        )}
      </Text>
    </Container>
  );
};

interface IOneTimeTextWithChip {
  chipText?: string;
  chipVariant?: ChipType["variant_"];
  title: string;
  text: string;
  children: ReactNode;
  onClick?: () => void;
  isSingleSuggestion?: boolean;
  selected?: boolean;
  sx?: SxProps<Theme>;
  discountChipText?: string;
  discountChipVariant?: ChipType["variant_"];
}

const PlanSuggestionTextWithChip = ({
  title,
  text,
  chipText,
  chipVariant,
  onClick,
  selected,
  isSingleSuggestion,
  children,
  sx,
  discountChipText,
  discountChipVariant,
}: IOneTimeTextWithChip) => {
  return (
    <PlanSuggestionCard selected={selected} onClick={onClick} isSingleSuggestion={isSingleSuggestion} sx={sx}>
      <Container fullWidth row>
        <Container fullWidth sx={{gap: "0px", alignItems: "start"}}>
          <Text variant={"inter600"}>{title}</Text>
          <Text>{text}</Text>
        </Container>
        <Container row sx={{gap: "4px"}}>
          {discountChipText && (
            <Chip label={discountChipText} variant_={discountChipVariant || "primary"} size_={"small"} />
          )}
          {chipText && <Chip label={chipText} variant_={chipVariant} size_={"small"} />}
        </Container>
      </Container>
      {children}
    </PlanSuggestionCard>
  );
};

export {
  SummarySectionTitle,
  SummaryText,
  BulletText,
  PlanSuggestion,
  TextLabel,
  HardshipSuggestion,
  PlanSuggestionCard,
  PlanSuggestionTextWithChip,
};
